// import function to register Swiper custom elements
import { register } from 'swiper/element/bundle';

import { Component, effect, inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { SplashScreen } from '@capacitor/splash-screen';
import {
  ActionSheetController,
  IonApp,
  IonRouterOutlet,
  ModalController,
  Platform,
  PopoverController,
  ToastController,
} from '@ionic/angular/standalone';

import { environment } from '@environments/environment';
import { NetworkService } from '@core/services/network/network.service';
import { ImpersonationService } from '@core/services/impersonation/impersonation.service';
import { StorageService } from '@core/services/storage/storage.service';
import { AppUpdateService } from './services/app-update.service';
import { AuthService } from './services/auth.service';
import { EventService } from './services/event.service';
import { LoadingService } from './services/loading.service';
import { NotificationService } from './services/notification.service';
import { StatusBarService } from './services/status-bar.service';
import { addIcons } from 'ionicons';
import { cloudOfflineOutline } from 'ionicons/icons';

// register Swiper custom elements
register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonRouterOutlet, IonApp],
  styles: [
    `
      .dev-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 99999;
        height: 18px;
        line-height: 18px;
        background: rgba(255, 107, 0, 0.9);
        color: #fff;
        text-align: center;
        font-size: 8px;
        font-weight: 700;
        letter-spacing: 2px;
        pointer-events: none;
      }
      /* Impersonation indicator lives at the top and RESERVES its own space (by
         pushing the router outlet down), so it never overlaps page headers or the
         bottom tab bar. */
      .impersonation-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 99998;
        box-sizing: border-box;
        height: calc(44px + env(safe-area-inset-top));
        padding: env(safe-area-inset-top) 14px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: #d9534f;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
      }
      .impersonation-banner button {
        background: #fff;
        color: #d9534f;
        border: none;
        border-radius: 6px;
        padding: 6px 12px;
        font-weight: 700;
        font-size: 13px;
      }
      ion-app.impersonating ion-router-outlet {
        top: calc(44px + env(safe-area-inset-top));
      }
      /* In dev builds the thin DEV strip occupies the very top; stack below it. */
      ion-app.with-dev .impersonation-banner {
        top: 18px;
      }
      ion-app.with-dev.impersonating ion-router-outlet {
        top: calc(44px + env(safe-area-inset-top) + 18px);
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  isDev = !environment.production;
  private platform = inject(Platform);
  private auth = inject(AuthService);
  private modalCtrl = inject(ModalController);
  private actionSheetCtrl = inject(ActionSheetController);
  private popoverCtrl = inject(PopoverController);
  private router = inject(Router);
  private toastController = inject(ToastController);
  private eventService = inject(EventService);
  private loadingService = inject(LoadingService);
  private appUpdateService = inject(AppUpdateService);
  private network = inject(NetworkService);
  private statusBarService = inject(StatusBarService);
  private notificationService = inject(NotificationService);
  private storage = inject(StorageService);
  protected impersonation = inject(ImpersonationService);

  swipe = true;
  private disconnectedToast: HTMLIonToastElement | null = null;

  // set up hardware back button event.
  lastTimeBackPress = 0;
  timePeriodToExit = 2000;
  loading: HTMLIonLoadingElement | null = null;

  @ViewChildren(IonRouterOutlet) routerOutlets: QueryList<IonRouterOutlet>;
  @ViewChild(IonRouterOutlet) routerOutlet: IonRouterOutlet;

  constructor() {
    addIcons({ cloudOfflineOutline });
    this.watchNetworkStatus();
  }

  ngOnInit(): void {
    this.initializeApp();
  }

  exitImpersonation(): void {
    this.auth.exitImpersonation();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.statusBarService.initialize();
      SplashScreen.hide();
      this.appUpdateService.checkForUpdate();
      // Register OneSignal click listener as early as possible so cold-start
      // notification taps are not lost while auth/network resolve.
      this.notificationService.initOneSignal();
      this.purgeLegacyRouteCache();
      this.impersonation.restore();

      this.eventService.watch.subscribe((value) => {
        if (value === 'user:logout') {
          this.auth.logout();
        }
        if (value === 'loading:present') {
          this.loadingService.present();
        }
        if (value === 'loading:dismiss') {
          this.loadingService.dismiss();
        }
        if (value === 'back:enable') {
          this.backButtonEvent2();
        }
      });
    });
  }

  backButtonEvent() {
    this.platform.backButton.subscribeWithPriority(0, () => {
      this.routerOutlets.forEach((outlet: IonRouterOutlet) => {
        /*if (this.router.url != '/tabs/tab1') {
          await this.router.navigate(['/tabs/tab1']);
        } else*/
        alert(this.router.url);
        if (this.router.url === '/tecservice/home') {
          this.auth.askExit();
        }
      });
    });
  }

  // active hardware back button
  backButtonEvent2() {
    this.platform.backButton.subscribeWithPriority(0, async () => {
      // close action sheet
      try {
        const element = await this.actionSheetCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        /* Intentionally ignore: element already dismissed */
      }

      // close popover
      try {
        const element = await this.popoverCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        /* Intentionally ignore: element already dismissed */
      }

      // close modal
      try {
        const element = await this.modalCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error);
      }

      this.routerOutlets.forEach(async (outlet: IonRouterOutlet) => {
        if (outlet && outlet.canGoBack()) {
          outlet.pop();
        } else {
          if (new Date().getTime() - this.lastTimeBackPress < this.timePeriodToExit) {
            // navigator['app'].exitApp(); // work for ionic 4
          } else {
            const toast3 = await this.toastController.create({
              message: 'Pulse atrás nuevamente para salir de la App.',
              duration: 2000,
              position: 'middle',
            });
            toast3.present();
            this.lastTimeBackPress = new Date().getTime();
          }
        }
      });
    });
  }

  /**
   * One-time cleanup of legacy on-device Google Routes API cache entries.
   * Polylines are now served by the backend; old `routes_*` keys would otherwise linger forever.
   */
  private async purgeLegacyRouteCache(): Promise<void> {
    try {
      const prefix = (environment.storagePrefix || '') + 'routes_';
      await this.storage.removeKeysWithPrefix(prefix);
    } catch (error) {
      console.warn('Failed to purge legacy route cache:', error);
    }
  }

  private watchNetworkStatus(): void {
    effect(async () => {
      const connected = this.network.isOnline();

      if (!connected) {
        if (this.disconnectedToast) return;
        this.disconnectedToast = await this.toastController.create({
          message: 'Sin conexión a internet',
          duration: 0,
          position: 'top',
          color: 'danger',
          icon: 'cloud-offline-outline',
        });
        await this.disconnectedToast.present();
      } else {
        if (this.disconnectedToast) {
          await this.disconnectedToast.dismiss();
          this.disconnectedToast = null;
        }
      }
    });
  }
}
